import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, CheckCircle, XCircle, Calendar, MapPin, QrCode, Download, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function TicketPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tickets/${ticketId}`).then(r => setTicket(r.data)).catch(() => toast.error("Ticket not found")).finally(() => setLoading(false));
  }, [ticketId]);

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading ticket...</div></div>;
  if (!ticket) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><p className="text-white/40 font-body">Ticket not found</p></div>;

  const isPaid = ticket.payment_status === "paid";
  const isUsed = ticket.used;

  return (
    <div data-testid="ticket-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-lg mx-auto px-6 py-16">
        <motion.div initial="hidden" animate="visible" className="space-y-6">
          {/* Ticket Card */}
          <motion.div variants={fadeUp} custom={0} className="glass-card overflow-hidden">
            {/* Header */}
            <div className="bg-gold/10 px-8 py-5 border-b border-gold/20 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Ticket size={20} className="text-gold" />
                <span className="text-gold text-[10px] font-body uppercase tracking-[0.2em]">E-Ticket</span>
              </div>
              <span className={`px-3 py-1 text-[10px] font-body uppercase tracking-wider ${isPaid ? (isUsed ? "bg-white/10 text-white/40" : "bg-green-500/15 text-green-400") : "bg-yellow-500/15 text-yellow-400"}`}>
                {isPaid ? (isUsed ? "Used" : "Valid") : "Pending Payment"}
              </span>
            </div>

            {/* Body */}
            <div className="p-8 space-y-6">
              <div>
                <h1 className="font-heading text-2xl text-white mb-1" data-testid="ticket-event-title">{ticket.event_title}</h1>
                <p className="text-gold/70 font-body text-sm">{ticket.ticket_type_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm font-body">
                <div><span className="text-white/30 text-xs uppercase block mb-1">Guest</span><span className="text-white/80" data-testid="ticket-buyer-name">{ticket.buyer_name}</span></div>
                <div><span className="text-white/30 text-xs uppercase block mb-1">Qty</span><span className="text-white/80">{ticket.quantity}</span></div>
                <div><span className="text-white/30 text-xs uppercase block mb-1">Total</span><span className="text-gold font-medium">${ticket.total_price?.toFixed(2)}</span></div>
                <div><span className="text-white/30 text-xs uppercase block mb-1">Ticket ID</span><span className="text-white/50 text-xs">{ticket.id?.slice(0, 8)}...</span></div>
              </div>

              {/* QR Code */}
              {isPaid && !isUsed && (
                <div className="flex flex-col items-center pt-4 border-t border-white/5">
                  <p className="text-white/30 text-xs font-body mb-4 uppercase tracking-wider">Show this at the door</p>
                  <img src={`${API}/tickets/${ticketId}/qr`} alt="E-Ticket QR" className="w-48 h-48" data-testid="ticket-qr-code" />
                  <a href={`${API}/tickets/${ticketId}/qr`} download={`ticket-${ticketId}.png`} className="mt-4 flex items-center gap-2 text-gold/60 hover:text-gold text-xs font-body transition-colors">
                    <Download size={14} /> Download QR Code
                  </a>
                </div>
              )}

              {isUsed && (
                <div className="flex flex-col items-center pt-4 border-t border-white/5">
                  <CheckCircle size={36} className="text-white/20 mb-2" />
                  <p className="text-white/30 font-body text-sm">This ticket has been used</p>
                  {ticket.used_at && <p className="text-white/20 font-body text-xs">Validated: {new Date(ticket.used_at).toLocaleString()}</p>}
                </div>
              )}

              {!isPaid && (
                <div className="flex flex-col items-center pt-4 border-t border-white/5">
                  <XCircle size={36} className="text-yellow-400/30 mb-2" />
                  <p className="text-white/40 font-body text-sm">Payment not yet completed</p>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} custom={1} className="text-center">
            <Link to={`/events/${ticket.event_id}`} data-testid="ticket-view-event" className="inline-flex items-center gap-2 text-white/40 hover:text-gold font-body text-sm transition-colors">
              View Event Page <ArrowRight size={14} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
